import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy } from '@angular/core';
import { gsap } from 'gsap';
import { registerRsPlugins, RsMotionService } from './rs-motion';

/**
 * Scroll choreography for the service-page zig-zag timeline.
 *
 * Attach to the `<section class="our-business">` (and its narrow twin) and it
 * animates whatever `.content-one` rows it finds — so it works unchanged on the
 * pages that have three rows, four rows, or a nested extra row.
 *
 * Per row it drives:
 * • the spine segment drawing itself (`--line` 0 → 1, consumed by a `scaleY`
 *   in CSS — the variable defaults to 1, so the line is fully drawn when JS
 *   never runs)
 * • the block itself fading in from x ±80 and scale 0.95
 * • a stagger on the heading and paragraph
 * • a slow parallax drift on the circular image
 *
 * One `gsap.context()`, reverted on destroy — no ScrollTrigger survives.
 */
@Directive({
  selector: '[rsServiceTimeline]',
  standalone: true,
})
export class RsServiceTimelineDirective implements AfterViewInit, OnDestroy {
  private ctx?: gsap.Context;

  constructor(
    private host: ElementRef<HTMLElement>,
    private motion: RsMotionService,
    private zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    if (!this.motion.enabled) return;

    const root = this.host.nativeElement;
    const rows = Array.from(root.querySelectorAll<HTMLElement>('.content-one'));
    if (!rows.length) return;

    this.motion.arm();
    registerRsPlugins();

    this.zone.runOutsideAngular(() => {
      this.ctx = gsap.context(() => {
        // section header
        gsap.from(root.querySelectorAll('.our-business-heading > * > *'), {
          opacity: 0,
          y: 24,
          duration: 0.7,
          stagger: 0.08,
          ease: 'power3.out',
          scrollTrigger: { trigger: root, start: 'top 84%', once: true },
        });

        rows.forEach((row) => this.buildRow(row));
      }, root);
    });
  }

  private buildRow(row: HTMLElement): void {
    // which side is the media on? that decides the slide direction
    const mediaLeft = !!row.querySelector('.center-line');
    const copy = row.querySelector<HTMLElement>('.float, .float-l');
    const media = row.querySelector<HTMLElement>('.center-line, .center-line-l');
    const node = row.querySelector<HTMLElement>('.s-no, .s-no-l');
    const img = row.querySelector<HTMLElement>('img');

    const tl = gsap.timeline({
      defaults: { ease: 'power3.out' },
      scrollTrigger: { trigger: row, start: 'top 82%', once: true },
    });

    // the spine segment draws downward
    tl.fromTo(row, { '--line': 0 }, { '--line': 1, duration: 0.7, ease: 'none' }, 0);

    if (media) {
      tl.from(
        media,
        {
          opacity: 0,
          x: mediaLeft ? -80 : 80,
          scale: 0.95,
          duration: 0.85,
          onStart: () => (media.style.willChange = 'transform, opacity'),
          onComplete: () => (media.style.willChange = 'auto'),
        },
        0
      );
    }

    if (node) {
      tl.from(node, { opacity: 0, scale: 0.5, duration: 0.6, ease: 'back.out(2)' }, 0.25);
    }

    if (copy) {
      tl.from(
        copy,
        {
          opacity: 0,
          x: mediaLeft ? 80 : -80,
          scale: 0.95,
          duration: 0.85,
          onStart: () => (copy.style.willChange = 'transform, opacity'),
          onComplete: () => (copy.style.willChange = 'auto'),
        },
        0.1
      ).from(
        copy.querySelectorAll('h3, p'),
        { opacity: 0, y: 18, duration: 0.5, stagger: 0.1 },
        '-=0.5'
      );
    }

    // slow parallax on the image as the row travels through the viewport
    if (img) {
      gsap.fromTo(
        img,
        { yPercent: -4 },
        {
          yPercent: 4,
          ease: 'none',
          scrollTrigger: {
            trigger: row,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
    }
  }

  ngOnDestroy(): void {
    this.ctx?.revert();
    this.ctx = undefined;
  }
}
